import React, { useState } from 'react';
import {
  generateInviteLinks,
  copyToClipboard,
  openWhatsApp,
  openEmail,
  PageInviteOptions
} from '../services/pageInviteService';
import { ShareIcon } from './icons';

interface PageInviteMenuItemProps {
  pageName?: string;
  pageHash?: string;
  userName?: string;
  onClose: () => void;
}

const PageInviteMenuItem: React.FC<PageInviteMenuItemProps> = ({
  pageName = 'deze pagina',
  pageHash,
  userName,
  onClose
}) => {
  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [inviteLinks, setInviteLinks] = useState<ReturnType<typeof generateInviteLinks> | null>(null);

  const handleClick = () => {
    const options: PageInviteOptions = {
      pageName,
      pageHash,
      customMessage: message || undefined,
      userName
    };
    
    const links = generateInviteLinks(options);
    setInviteLinks(links);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCopied(false);
    onClose(); // Sluit ook het profielmenu
  };

  const handleCopyLink = async () => {
    if (!inviteLinks) return;
    const success = await copyToClipboard(inviteLinks.url);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleWhatsApp = () => {
    if (!inviteLinks) return;
    openWhatsApp(inviteLinks.whatsappUrl);
    handleCloseModal();
  };

  const handleEmail = () => {
    if (!inviteLinks) return;
    openEmail(inviteLinks.emailLink);
    handleCloseModal();
  };

  const handleUpdateMessage = (newMessage: string) => {
    setMessage(newMessage);
    const options: PageInviteOptions = {
      pageName,
      pageHash,
      customMessage: newMessage || undefined,
      userName
    };
    setInviteLinks(generateInviteLinks(options));
  };

  return (
    <>
      <button
        onClick={handleClick}
        className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 transition-colors text-gray-700"
      >
        <span className="flex items-center gap-2"><ShareIcon size={16} weight="regular" className="text-gray-500" /> Deel pagina</span>
      </button>

      {showModal && inviteLinks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Deel deze pagina
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Link:
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={inviteLinks.url}
                  readOnly
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    copied
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {copied ? '✓' : 'Kopieer'}
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bericht (optioneel):
              </label>
              <textarea
                value={message}
                onChange={(e) => handleUpdateMessage(e.target.value)}
                placeholder={`Hoi! Ik wil je graag uitnodigen om ${pageName} te bekijken op SuperKonnected.`}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                rows={3}
              />
            </div>

            <div className="space-y-3">
              <button
                onClick={handleWhatsApp}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <span className="text-xl">💬</span>
                <span>Deel via WhatsApp</span>
              </button>

              <button
                onClick={handleEmail}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <span className="text-xl">📧</span>
                <span>Deel via Email</span>
              </button>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                De link werkt op alle apparaten en opent automatisch de juiste pagina
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PageInviteMenuItem;
